<div class="page-content">
  <header class="page-header">
    <a class="back-link" (click)="goBack()" tabindex="0" (keydown.enter)="goBack()">← Clients</a>
    <h1 class="page-title">{{ clientFormPageTitle }}</h1>
    <p class="page-description">
      {{ clientFormPageDescription }}
    </p>
  </header>

  <div
    class="client-form-card-toolbar"
    *ngIf="isEditMode && !clientPageViewOnly && !pageEditUnlocked">
    <button type="button" class="button" [disabled]="isLoadingVoiceConcurrency" (click)="startPageEdit()">Edit</button>
    <button type="button" class="button" (click)="openAddCreditsModal()">Add credits</button>
  </div>

  <section class="card client-form-card">
      <div class="client-form-card__header">
        <h2 class="card__title">Basic information</h2>
        <div class="client-badges" *ngIf="isEditMode && clientCode">
          <span class="client-badge">{{ clientCode }}</span>
          <span
            class="client-badge client-badge--credits"
            [attr.title]="clientCreditsLoadMessage || 'Available wallet credits'">
            {{ clientCreditsDisplayLabel }}
          </span>
        </div>
      </div>
      <form [formGroup]="clientForm" (ngSubmit)="onClientFormSubmit($event)" class="client-form">
        <ng-template #residencyAndVoiceFields>
          <div class="form-group form-group--data-residency">
            <span class="form-label" id="data-residency-label">Data Residency</span>
            <input type="hidden" formControlName="dataResidency" />
            <div
              class="residency-dd"
              [class.residency-dd--open]="dataResidencyMenuOpen"
              #residencyDropdownRoot>
              <button
                type="button"
                class="residency-dd__trigger"
                id="data-residency-trigger"
                (click)="toggleDataResidencyMenu($event)"
                [disabled]="pageFieldsReadOnly || isLoadingDataResidencyOptions"
                [attr.aria-expanded]="dataResidencyMenuOpen"
                aria-haspopup="listbox"
                aria-controls="data-residency-listbox"
                aria-labelledby="data-residency-label">
                <span class="residency-dd__value">{{ dataResidencyDisplayLabel }}</span>
                <span class="residency-dd__chevron" aria-hidden="true">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </span>
              </button>
              <ul
                *ngIf="dataResidencyMenuOpen"
                class="residency-dd__list"
                id="data-residency-listbox"
                role="listbox"
                aria-labelledby="data-residency-label">
                <li
                  *ngFor="let opt of dataResidencyOptions"
                  class="residency-dd__option"
                  [class.residency-dd__option--selected]="isDataResidencySelected(opt.value)"
                  role="option"
                  [attr.aria-selected]="isDataResidencySelected(opt.value)"
                  (click)="selectDataResidency(opt.value)">
                  {{ opt.label }}
                </li>
              </ul>
            </div>
            <small class="field-help" *ngIf="isLoadingDataResidencyOptions">
              Loading regions…
            </small>
          </div>

          <label
            class="form-group form-group--data-residency form-group--voice-concurrency"
            *ngIf="isAgentEnabled('voice')">
            <span class="form-label">Voice concurrency</span>
            <input
              type="number"
              formControlName="voiceConcurrency"
              class="form-input form-input--fit-label"
              min="1"
              max="1000"
              step="1"
              inputmode="numeric"
              list="voice-concurrency-presets"
              placeholder="e.g. 10"
              [disabled]="pageFieldsReadOnly || isLoadingVoiceConcurrency" />
            <datalist id="voice-concurrency-presets">
              <option *ngFor="let n of voiceConcurrencyOptions" [attr.value]="n"></option>
            </datalist>
            <small class="field-help" *ngIf="isLoadingVoiceConcurrency">
              Loading voice concurrency…
            </small>
            <small class="field-error" *ngIf="voiceConcurrencyLoadMessage">
              {{ voiceConcurrencyLoadMessage }}
            </small>
          </label>

          <div
            class="form-group form-group--data-residency form-group--voice-stack"
            *ngIf="isAgentEnabled('voice')">
            <span class="form-label" id="voice-stack-label">Voice stack</span>
            <input type="hidden" formControlName="voiceStackId" />
            <div
              class="residency-dd voice-stack-dd"
              [class.residency-dd--open]="voiceStackMenuOpen"
              #voiceStackDropdownRoot>
              <button
                type="button"
                class="residency-dd__trigger"
                id="voice-stack-trigger"
                (click)="toggleVoiceStackMenu($event)"
                [disabled]="pageFieldsReadOnly || isLoadingVoiceStacks || voiceStackSelectOptions.length === 0"
                [attr.aria-expanded]="voiceStackMenuOpen"
                aria-haspopup="listbox"
                aria-controls="voice-stack-listbox"
                aria-labelledby="voice-stack-label">
                <span class="residency-dd__value">{{ voiceStackDisplayLabel || 'Select stack' }}</span>
                <span class="residency-dd__chevron" aria-hidden="true">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </span>
              </button>
              <ul
                *ngIf="voiceStackMenuOpen"
                class="residency-dd__list"
                id="voice-stack-listbox"
                role="listbox"
                aria-labelledby="voice-stack-label">
                <li
                  *ngFor="let opt of voiceStackSelectOptions"
                  class="residency-dd__option"
                  [class.residency-dd__option--selected]="isVoiceStackSelected(opt.value)"
                  role="option"
                  [attr.aria-selected]="isVoiceStackSelected(opt.value)"
                  (click)="selectVoiceStack(opt.value)">
                  {{ opt.label }}
                </li>
              </ul>
            </div>
            <small class="field-help" *ngIf="isLoadingVoiceStacks">
              Loading voice stacks...
            </small>
            <small class="field-help" *ngIf="!isLoadingVoiceStacks && effectiveVoiceStackDisplayLabel">
              Effective stack: {{ effectiveVoiceStackDisplayLabel }}
            </small>
          </div>
        </ng-template>

        <label class="form-group form-group--client-name">
          <span class="form-label">Client Name</span>
          <input
            type="text"
            formControlName="clientName"
            placeholder="Enter client name"
            class="form-input"
            [disabled]="pageFieldsReadOnly" />
          <small
            class="field-error"
            *ngIf="clientForm.controls['clientName'].touched && clientForm.controls['clientName'].invalid">
            Client name must be at least 3 characters long.
          </small>
        </label>

        <div class="form-group form-group--enabled-agents" *ngIf="isEditMode || !clientPageViewOnly">
          <span class="form-label" id="enabled-agents-label">Enabled Agents</span>
          <div
            class="agent-checkboxes"
            role="group"
            aria-labelledby="enabled-agents-label">
            <label *ngFor="let agent of availableAgents" class="agent-checkbox-row">
              <input
                type="checkbox"
                [checked]="isAgentEnabled(agent.value)"
                [disabled]="pageFieldsReadOnly"
                (change)="toggleEnabledAgent(agent.value, $event)" />
              <span class="agent-checkbox-row__text">{{ agent.label }}</span>
            </label>
          </div>
        </div>

        <ng-container *ngIf="!isEditMode && !clientPageViewOnly">
          <ng-container *ngTemplateOutlet="residencyAndVoiceFields"></ng-container>
        </ng-container>

        <div class="basic-info-post-agents" *ngIf="isEditMode">
          <ng-container *ngTemplateOutlet="residencyAndVoiceFields"></ng-container>
        </div>

        <div class="message message--error" *ngIf="errorMessage">{{ errorMessage }}</div>
        <div class="message message--success" *ngIf="successMessage">{{ successMessage }}</div>

        <!-- Keeps Enter in basic fields submitting the form; primary actions are below Pricing & Billing. -->
        <button
          *ngIf="!isEditMode && !clientPageViewOnly"
          type="submit"
          class="client-form__hidden-submit"
          tabindex="-1"
          aria-hidden="true"
          [disabled]="isSubmitting || isCreateClientBlockedByPricing">
          Create
        </button>
      </form>
  </section>

  <section class="card pricing-management" *ngIf="isEditMode || !clientPageViewOnly">
        <div class="card__header">
          <h2 class="card__title">Pricing & Billing</h2>
          <p class="card__description" *ngIf="clientPageViewOnly">
            Billing tier and custom pricing for this client (read-only). Data is loaded from the server when you open
            View.
          </p>
          <p class="card__description" *ngIf="isEditMode && !clientPageViewOnly">
            Set billing tier and custom credit-per-unit pricing for this client.
          </p>
          <p class="card__description" *ngIf="!isEditMode">
            Optional billing overrides are saved with the client. Leave custom pricing empty to use server defaults.
          </p>
        </div>

        <div class="pricing-form">
          <div class="billing-tier-block">
            <div class="form-group form-group--billing-tier">
              <span class="form-label" id="billing-tier-label">Billing tier</span>
              <div
                class="residency-dd billing-tier-dd"
                [class.residency-dd--open]="billingTierMenuOpen">
                <button
                  type="button"
                  class="residency-dd__trigger"
                  id="billing-tier-trigger"
                  (click)="toggleBillingTierMenu($event)"
                  [disabled]="pageFieldsReadOnly"
                  [attr.aria-expanded]="billingTierMenuOpen"
                  aria-haspopup="listbox"
                  aria-controls="billing-tier-listbox"
                  aria-labelledby="billing-tier-label">
                  <span class="residency-dd__value">{{ billingTierDisplayLabel }}</span>
                  <span class="residency-dd__chevron" aria-hidden="true">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </span>
                </button>
                <ul
                  *ngIf="billingTierMenuOpen"
                  class="residency-dd__list"
                  id="billing-tier-listbox"
                  role="listbox"
                  aria-labelledby="billing-tier-label">
                  <li
                    *ngFor="let t of BILLING_TIERS"
                    class="residency-dd__option"
                    [class.residency-dd__option--selected]="billingTier === t.value"
                    role="option"
                    [attr.aria-selected]="billingTier === t.value"
                    (click)="selectBillingTier(t.value)">
                    {{ t.label }}
                  </li>
                </ul>
              </div>
            </div>
            <div class="form-group form-group--checkbox pricing-allow-negative">
              <label class="checkbox-label">
                <input type="checkbox" [(ngModel)]="allowNegativeBalance" [ngModelOptions]="{ standalone: true }" class="checkbox-input" [disabled]="pageFieldsReadOnly" />
                <span>Allow negative balance</span>
              </label>
            </div>
            <div class="form-group form-group--checkbox pricing-base-credit-prompt-builder">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  [(ngModel)]="baseCreditUsagePromptBuilder"
                  [ngModelOptions]="{ standalone: true }"
                  class="checkbox-input"
                  [disabled]="pageFieldsReadOnly" />
                <span>Prompt builder base credit usage</span>
              </label>
            </div>
          </div>

          <div class="custom-pricing-block">
            <h3 class="block-title">Custom pricing (credits per unit)</h3>
            <p class="field-help" *ngIf="!pageFieldsReadOnly && !clientPageViewOnly && !isEditMode">
              Default feature/unit rows come from settings meta. Rows with credits greater than 0 are saved as pricing overrides.
            </p>
            <p class="field-help" *ngIf="!pageFieldsReadOnly && !clientPageViewOnly && isEditMode">
              Add rules for feature + unit type. These are sent as custom pricing on the billing API (no rows means an
              empty map).
            </p>
            <p class="field-help" *ngIf="isEditMode && pageFieldsReadOnly && !clientPageViewOnly">
              Billing is read-only until you click Edit. After editing, use Save below this table to update the client
              and billing together.
            </p>
            <p class="field-help" *ngIf="clientPageViewOnly">
              Rules stored in custom pricing for this client. Empty means no custom rules are configured.
            </p>
            <div
              class="pricing-rules-wrap"
              [class.pricing-rules-wrap--view-only]="pageFieldsReadOnly">
              <div class="pricing-rule-header">
                <span>Feature</span>
                <span>Unit type</span>
                <span>Credits</span>
                <span>Rounding</span>
                <span>Interval (s)</span>
                <span>Min (s)</span>
                <span *ngIf="!pageFieldsReadOnly" class="pricing-rule-header__actions"></span>
              </div>
              <div class="pricing-rule-row" *ngFor="let rule of pricingRules; let i = index">
                <div
                  class="residency-dd pricing-rule-dd"
                  [class.residency-dd--open]="isPricingDdOpen(i, 'feature')">
                  <button
                    type="button"
                    class="residency-dd__trigger"
                    (click)="togglePricingDd($event, i, 'feature')"
                    [disabled]="pageFieldsReadOnly"
                    [attr.aria-expanded]="isPricingDdOpen(i, 'feature')"
                    aria-haspopup="listbox"
                    [attr.aria-controls]="'pricing-feature-' + i"
                    [attr.aria-label]="'Feature: ' + pricingRuleFieldLabelForRule(rule, 'feature')">
                    <span class="residency-dd__value">{{ pricingRuleFieldLabelForRule(rule, 'feature') }}</span>
                    <span class="residency-dd__chevron" aria-hidden="true">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                    </span>
                  </button>
                  <ul
                    *ngIf="isPricingDdOpen(i, 'feature')"
                    class="residency-dd__list"
                    [id]="'pricing-feature-' + i"
                    role="listbox"
                    aria-label="Feature">
                    <li
                      *ngFor="let f of pricingFeatureOptions"
                      class="residency-dd__option"
                      [class.residency-dd__option--selected]="rule.featureCode === f.value"
                      role="option"
                      [attr.aria-selected]="rule.featureCode === f.value"
                      (click)="selectPricingRuleDropdown(i, 'feature', f.value)">
                      {{ f.label }}
                    </li>
                  </ul>
                </div>
                <div
                  class="residency-dd pricing-rule-dd"
                  [class.residency-dd--open]="isPricingDdOpen(i, 'unit')">
                  <button
                    type="button"
                    class="residency-dd__trigger"
                    (click)="togglePricingDd($event, i, 'unit')"
                    [disabled]="pageFieldsReadOnly"
                    [attr.aria-expanded]="isPricingDdOpen(i, 'unit')"
                    aria-haspopup="listbox"
                    [attr.aria-controls]="'pricing-unit-' + i"
                    [attr.aria-label]="'Unit type: ' + pricingRuleFieldLabelForRule(rule, 'unit')">
                    <span class="residency-dd__value">{{ pricingRuleFieldLabelForRule(rule, 'unit') }}</span>
                    <span class="residency-dd__chevron" aria-hidden="true">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                    </span>
                  </button>
                  <ul
                    *ngIf="isPricingDdOpen(i, 'unit')"
                    class="residency-dd__list residency-dd__list--scrollable"
                    [id]="'pricing-unit-' + i"
                    role="listbox"
                    aria-label="Unit type">
                    <li
                      *ngFor="let u of pricingUnitOptionsForRule(rule)"
                      class="residency-dd__option"
                      [class.residency-dd__option--selected]="rule.unitType === u.value"
                      role="option"
                      [attr.aria-selected]="rule.unitType === u.value"
                      (click)="selectPricingRuleDropdown(i, 'unit', u.value)">
                      {{ u.label }}
                    </li>
                  </ul>
                </div>
                <input
                  type="number"
                  [(ngModel)]="rule.creditPerUnit"
                  [ngModelOptions]="{ standalone: true }"
                  class="form-input pricing-rule__control"
                  [class.form-input--invalid]="pricingRuleHasInvalidCredits(rule)"
                  [attr.aria-invalid]="pricingRuleHasInvalidCredits(rule) ? 'true' : null"
                  placeholder="Credits"
                  min="0"
                  step="0.01"
                  autocomplete="off"
                  [attr.name]="'pricing-credit-' + i"
                  [disabled]="pageFieldsReadOnly">
                <div
                  class="residency-dd pricing-rule-dd"
                  [class.residency-dd--open]="isPricingDdOpen(i, 'rounding')">
                  <button
                    type="button"
                    class="residency-dd__trigger"
                    (click)="togglePricingDd($event, i, 'rounding')"
                    [disabled]="pageFieldsReadOnly"
                    [attr.aria-expanded]="isPricingDdOpen(i, 'rounding')"
                    aria-haspopup="listbox"
                    [attr.aria-controls]="'pricing-rounding-' + i"
                    [attr.aria-label]="'Rounding: ' + pricingRuleFieldLabelForRule(rule, 'rounding')">
                    <span class="residency-dd__value">{{ pricingRuleFieldLabelForRule(rule, 'rounding') }}</span>
                    <span class="residency-dd__chevron" aria-hidden="true">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                    </span>
                  </button>
                  <ul
                    *ngIf="isPricingDdOpen(i, 'rounding')"
                    class="residency-dd__list"
                    [id]="'pricing-rounding-' + i"
                    role="listbox"
                    aria-label="Rounding">
                    <li
                      *ngFor="let r of ROUNDING_TYPES"
                      class="residency-dd__option"
                      [class.residency-dd__option--selected]="rule.rounding === r.value"
                      role="option"
                      [attr.aria-selected]="rule.rounding === r.value"
                      (click)="selectPricingRuleDropdown(i, 'rounding', r.value)">
                      {{ r.label }}
                    </li>
                  </ul>
                </div>
                <ng-container *ngIf="rule.featureCode === 'voice'; else noIntervalMin">
                  <input
                    type="number"
                    [(ngModel)]="rule.intervalSeconds"
                    [ngModelOptions]="{ standalone: true }"
                    class="form-input pricing-rule__control"
                    placeholder="Interval (s)"
                    min="1"
                    title="Optional: billing interval for seconds-based units"
                    [disabled]="pageFieldsReadOnly">
                  <input
                    type="number"
                    [(ngModel)]="rule.minimumSeconds"
                    [ngModelOptions]="{ standalone: true }"
                    class="form-input pricing-rule__control"
                    placeholder="Min (s)"
                    min="0"
                    title="Optional: minimum duration for seconds-based units"
                    [disabled]="pageFieldsReadOnly">
                </ng-container>
                <ng-template #noIntervalMin>
                  <span class="pricing-rule-placeholder">—</span>
                  <span class="pricing-rule-placeholder">—</span>
                </ng-template>
                <button
                  *ngIf="!pageFieldsReadOnly"
                  type="button"
                  class="button button-transparent button-small"
                  (click)="removePricingRule(i)">
                  Remove
                </button>
              </div>
            </div>
            <p class="field-error pricing-validation-message" *ngIf="!pageFieldsReadOnly && hasInvalidPricingCredits">
              Credits must be greater than 0 for each custom pricing rule.
            </p>
            <p
              class="field-error pricing-validation-message"
              *ngIf="!pageFieldsReadOnly && pricingFeatureOptions.length === 0">
              Select Chat Agent or Voice Agent before adding custom pricing.
            </p>
            <button
              type="button"
              class="button button-secondary"
              *ngIf="!pageFieldsReadOnly && isEditMode"
              (click)="addPricingRule()"
              [disabled]="pageFieldsReadOnly || pricingFeatureOptions.length === 0">
              + Add rule
            </button>
          </div>

          <div class="message message--error" *ngIf="pricingErrorMessage">{{ pricingErrorMessage }}</div>
          <div class="message message--success" *ngIf="pricingSuccessMessage">{{ pricingSuccessMessage }}</div>

          <div class="pricing-form__page-actions" *ngIf="isEditMode && !clientPageViewOnly && pageEditUnlocked">
            <button
              type="button"
              class="button button-transparent"
              [disabled]="isSavingEntirePage || isLoadingVoiceConcurrency"
              (click)="cancelPageEdit()">
              Cancel
            </button>
            <button type="button" class="button" [disabled]="isSavingEntirePage || isLoadingVoiceConcurrency" (click)="saveEntireEditPage()">
              {{ isSavingEntirePage ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </div>
  </section>

  <div class="create-page-footer-actions" *ngIf="!isEditMode && !clientPageViewOnly">
    <button type="button" class="button button-transparent" (click)="goBack()">Cancel</button>
    <button
      type="button"
      class="button"
      [disabled]="isSubmitting || isCreateClientBlockedByPricing"
      (click)="onSubmit()">
      {{ isSubmitting ? 'Creating...' : 'Create Client' }}
    </button>
  </div>

  <div
    class="add-credits-modal-backdrop"
    *ngIf="addCreditsModalOpen"
    (click)="onAddCreditsBackdropClick($event)"
    role="presentation">
    <div
      class="card add-credits-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-credits-heading"
      (click)="$event.stopPropagation()">
      <h2 id="add-credits-heading" class="card__title">Add credits</h2>
      <p class="add-credits-modal__client-name">{{ clientForm.get('clientName')?.value }}</p>

      <div class="client-form add-credits-modal__form">
        <label class="form-group">
          <span class="form-label">Credits</span>
          <input
            type="number"
            class="form-input"
            [(ngModel)]="addCreditsAmount"
            [ngModelOptions]="{standalone: true}"
            min="0.01"
            step="0.01"
            inputmode="decimal"
            placeholder="e.g. 100"
            [disabled]="addCreditsSubmitting" />
        </label>

        <div class="form-group form-group--add-credits-feature">
          <span class="form-label form-label--feature" id="add-credits-feature-label">Feature (optional)</span>
          <div
            class="residency-dd add-credits-feature-dd"
            [class.residency-dd--open]="addCreditsFeatureMenuOpen">
            <button
              type="button"
              class="residency-dd__trigger add-credits-feature-dd__trigger"
              id="add-credits-feature-trigger"
              (click)="toggleAddCreditsFeatureMenu($event)"
              [disabled]="addCreditsSubmitting"
              [attr.aria-expanded]="addCreditsFeatureMenuOpen"
              aria-haspopup="listbox"
              aria-controls="add-credits-feature-listbox"
              aria-labelledby="add-credits-feature-label">
              <span class="residency-dd__value">{{ addCreditsFeatureDisplayLabel }}</span>
              <span
                class="residency-dd__chevron add-credits-feature-dd__chevron"
                [class.add-credits-feature-dd__chevron--open]="addCreditsFeatureMenuOpen"
                aria-hidden="true">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    stroke-width="1.35"
                    stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </span>
            </button>
            <ul
              *ngIf="addCreditsFeatureMenuOpen"
              class="residency-dd__list add-credits-feature-dd__list"
              id="add-credits-feature-listbox"
              role="listbox"
              aria-labelledby="add-credits-feature-label">
              <li
                class="residency-dd__option add-credits-feature-dd__option"
                [class.residency-dd__option--selected]="!addCreditsFeatureCode"
                role="option"
                [attr.aria-selected]="!addCreditsFeatureCode"
                (click)="selectAddCreditsFeature('')">
                All
              </li>
              <li
                *ngFor="let f of FEATURE_CODES"
                class="residency-dd__option add-credits-feature-dd__option"
                [class.residency-dd__option--selected]="addCreditsFeatureCode === f.value"
                role="option"
                [attr.aria-selected]="addCreditsFeatureCode === f.value"
                (click)="selectAddCreditsFeature(f.value)">
                {{ f.label }}
              </li>
            </ul>
          </div>
        </div>

        <label class="form-group">
          <span class="form-label">Expiry date (optional)</span>
          <input
            type="date"
            class="form-input"
            [(ngModel)]="addCreditsExpiry"
            [ngModelOptions]="{standalone: true}"
            [disabled]="addCreditsSubmitting" />
        </label>

        <div class="message message--error" *ngIf="addCreditsError">{{ addCreditsError }}</div>

        <div class="add-credits-modal__actions">
          <button
            type="button"
            class="button button-transparent"
            [disabled]="addCreditsSubmitting"
            (click)="closeAddCreditsModal()">
            Cancel
          </button>
          <button
            type="button"
            class="button"
            [disabled]="addCreditsSubmitting"
            (click)="submitAddCredits()">
            {{ addCreditsSubmitting ? 'Adding…' : 'Add credits' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
